import axios from 'axios';
import { withFreshAuth } from './freshAuth';
import { ENDPOINTS } from '../config/endpoints';

const API_BASE_URL = ENDPOINTS.metrics;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

api.interceptors.request.use(withFreshAuth);

export const metricsService = {
  // Обращения пользователей. Адрес автора не передаём — бэкенд берёт его из
  // токена: поле, которое заполняет клиент, клиент заполнит мусором.
  async sendFeedback({ type, text, context, clientVersion }) {
    const response = await api.post('/feedback', {
      type, text, context: context || {}, client_version: clientVersion || null,
    });
    return response.data;
  },
  // Скриншот к обращению — отдельным запросом ПОСЛЕ приёма текста: отказ по
  // размеру не должен терять само обращение. Тип бэкенд определяет по байтам.
  async sendFeedbackAttachment(id, file) {
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await api.post(`/feedback/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async getMyFeedback() {
    const response = await api.get('/feedback/mine');
    return response.data;
  },
  // Счётчик непрочитанных решений: интерфейс зовёт его регулярно, поэтому он
  // отдельный и лёгкий — тянуть весь список ради одной цифры незачем.
  async getUnreadFeedback() {
    const response = await api.get('/feedback/unread');
    return response.data;
  },
  async markFeedbackSeen(id) {
    const response = await api.post(`/feedback/${id}/seen`);
    return response.data;
  },
  // Человек раскрыл сообщение из блока «Что нового» в окне обращений.
  async markAnnouncementSeen(id) {
    const response = await api.post(`/announcements/${id}/seen`);
    return response.data;
  },

  async getOverview(params = {}) {
    const response = await api.get('/overview', { params });
    return response.data;
  },

  async getRates() {
    const response = await api.get('/admin/rates');
    return response.data;
  },
  async putRate(rate) {
    const response = await api.put('/admin/rates', rate);
    return response.data;
  },
  async deleteRate(rate) {
    const response = await api.delete('/admin/rates', { data: rate });
    return response.data;
  },
  async getOfficeShare(month) {
    // month 'YYYY-MM' → { month, rows, by_office, total_share_rub, ... } (bit-metrics office_share.py)
    const response = await api.get('/admin/reports/office-share', { params: month ? { month } : {} });
    return response.data;
  },
  async getOfficeMap() {
    const response = await api.get('/admin/office-map');
    return response.data;
  },
  async putOffice(body) {
    // body: { account, office }  (пустой office снимает закрепление)
    const response = await api.put('/admin/office-map', body);
    return response.data;
  },
  async resolveOffices(emails) {
    // emails[] → { email_lower: office|null } (office_map + org-directory)
    const response = await api.post('/office/resolve', { emails });
    return response.data.offices || {};
  },
  async resolveEnrichment(emails) {
    // emails[] → { offices: {email: office|null}, people: {email: {name, department, job_title}} }
    const response = await api.post('/office/resolve', { emails });
    return { offices: response.data.offices || {}, people: response.data.people || {} };
  },

  async getUsers(params = {}) {
    const response = await api.get('/users', { params });
    return response.data;
  },

  async getUserSummary(email) {
    const response = await api.get(`/users/${encodeURIComponent(email)}/summary`);
    return response.data;
  },

  async getUserTasks(email, params = {}) {
    const response = await api.get(`/users/${encodeURIComponent(email)}/tasks`, { params });
    return response.data;
  },

  async getTimeseries(params = {}) {
    const response = await api.get('/timeseries', { params });
    return response.data;
  },

  async getQuota(email) {
    const response = await api.get(`/quota/${encodeURIComponent(email)}`);
    return response.data;
  },

  async getOrgUsage(orgId) {
    // Расход организации поимённо за период тарифа. Отдаётся плательщику и
    // админу Лаборатории: это персональные данные сотрудников клиента.
    const response = await api.get(`/organizations/${encodeURIComponent(orgId)}/usage`);
    return response.data;
  },

  async postUiEvent(event) {
    const response = await api.post('/ui-event', event);
    return response.data;
  },

  async getAbandoned(params = {}) {
    const response = await api.get('/abandoned', { params });
    return response.data;
  },
};
